import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const MODES = [
  { value: "single_narrator", label: "Single Narrator", hint: "Scott performs every role (House Diamond default)" },
  { value: "hybrid", label: "Hybrid", hint: "Major characters get dedicated voices; others stay with Scott" },
  { value: "full_cast", label: "Full Cast", hint: "Every speaking role may receive its own voice" },
];

export function CreateProjectModal({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [mode, setMode] = useState("single_narrator");
  const [wordCount, setWordCount] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle("");
    setAuthor("");
    setMode("single_narrator");
    setWordCount("");
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error("A project title is required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/projects", {
        title: title.trim(),
        author: author.trim() || null,
        production_mode: mode,
        target_word_count: wordCount ? parseInt(wordCount, 10) : null,
      });
      toast.success(`Project "${data.title}" created.`);
      reset();
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-amber-500/15 bg-[#12161f] text-slate-100 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">New production</DialogTitle>
          <DialogDescription className="text-slate-400">
            Create a private book project. The locked Scott narrator preset is attached automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="p-title" className="text-xs uppercase tracking-wider text-slate-400">
              Book title
            </Label>
            <Input
              id="p-title"
              data-testid="project-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="House Diamond"
              className="bg-slate-950/60"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-author" className="text-xs uppercase tracking-wider text-slate-400">
              Author
            </Label>
            <Input
              id="p-author"
              data-testid="project-author-input"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Rik Foote"
              className="bg-slate-950/60"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400">Production mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="production-mode-select" className="bg-slate-950/60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-border bg-[#12161f] text-slate-100">
                {MODES.map((m) => (
                  <SelectItem key={m.value} value={m.value} data-testid={`mode-option-${m.value}`}>
                    <div className="flex flex-col">
                      <span>{m.label}</span>
                      <span className="text-xs text-slate-500">{m.hint}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-words" className="text-xs uppercase tracking-wider text-slate-400">
              Target word count (optional)
            </Label>
            <Input
              id="p-words"
              type="number"
              data-testid="project-wordcount-input"
              value={wordCount}
              onChange={(e) => setWordCount(e.target.value)}
              placeholder="e.g. 90000"
              className="bg-slate-950/60"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">
            Cancel
          </Button>
          <Button
            data-testid="save-project-button"
            onClick={submit}
            disabled={saving}
            className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Create project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
